<template>
  <Teleport to="body">
    <transition name="fade-overlay">
      <div v-if="visible" class="modal-overlay" @click="closeModal">
      <div class="modal follow-modal" @click.stop>
        <div class="modal-header">
          <h3>{{ type === 'following' ? '我关注的人' : '我的粉丝' }}</h3>
          <button class="close-btn" @click="closeModal">×</button>
        </div>
        <div v-if="loading" class="empty-state"><p>加载中...</p></div>
        <div v-else-if="users.length === 0" class="empty-state">
          <div class="empty-icon">{{ type === 'following' ? '👥' : '🙋' }}</div>
          <p>{{ type === 'following' ? '还没有关注任何人' : '还没有粉丝' }}</p>
        </div>
        <div v-else class="follow-flex">
          <div
            v-for="u in users"
            :key="u.userId"
            class="follow-item"
          >
            <div class="avatar-wrap">
              <img 
                v-if="shouldShowAvatar(u)" 
                :src="getAvatarSrc(u)" 
                class="avatar-small-img" 
                @error="handleAvatarError(u)" 
              />
              <div v-else class="avatar-small">{{ (u.username || '').charAt(0).toUpperCase() }}</div>
            </div>
            <div class="user-info">
              <div class="list-username" @click="viewProfile(u.userId)">{{ u.username }}</div>
              <div class="status" :class="{ online: u.isOnline }">
                {{ u.isOnline ? '在线' : '离线' }}
              </div>
            </div>
            <div class="actions">
              <button class="view-btn" @click="viewProfile(u.userId)">查看主页</button>
              <button v-if="type === 'following'" class="unfollow-btn" @click="unfollow(u.userId)">取消关注</button>
            </div>
          </div>
        </div>
        
        <!-- 分页控件 -->
        <div class="pagination-wrapper" v-if="total > pageSize && !loading">
          <button class="page-btn" :disabled="currentPage === 1" @click="changePage(currentPage - 1)">
            <i class="fa-solid fa-chevron-left"></i> 上一页
          </button>
          <span class="page-info">{{ currentPage }} / {{ Math.ceil(total / pageSize) }}</span>
          <button class="page-btn" :disabled="currentPage >= Math.ceil(total / pageSize)" @click="changePage(currentPage + 1)">
            下一页 <i class="fa-solid fa-chevron-right"></i>
          </button>
        </div>
      </div>
    </div>
  </transition>
  </Teleport>
</template>

<script>
import { reactive } from 'vue'
import { useRouter } from 'vue-router'
import { avatarUrl } from '@/assets/BaseServerUrl.js'

const AVATAR_EXTENSIONS = ['webp', 'png', 'jpg', 'jpeg', 'gif']

export default {
  name: 'FollowModal',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    type: {
      type: String,
      default: 'following' // 'following' or 'followers'
    },
    loading: {
      type: Boolean,
      default: false
    },
    users: {
      type: Array,
      default: () => []
    },
    currentPage: {
      type: Number,
      default: 1
    },
    pageSize: {
      type: Number,
      default: 20
    },
    total: {
      type: Number,
      default: 0
    }
  },
  emits: ['update:visible', 'page-change', 'unfollow', 'close'],
  setup(props, { emit }) {
    const router = useRouter()
    const avatarState = reactive({})

    const closeModal = () => {
      emit('update:visible', false)
      emit('close')
    }

    const changePage = (pageNumber) => {
      if (pageNumber >= 1 && pageNumber <= Math.ceil(props.total / props.pageSize)) {
        emit('page-change', pageNumber)
      }
    }

    const unfollow = (userId) => {
      emit('unfollow', userId)
    }

    const viewProfile = (userId) => {
      closeModal()
      router.push(`/user/${userId}`)
    }

    const getAvatarState = (user) => {
      if (!user || !user.userId) return { error: true }
      if (!avatarState[user.userId]) {
        avatarState[user.userId] = { extIndex: 0, error: false }
      }
      return avatarState[user.userId]
    }

    const shouldShowAvatar = (user) => {
      const state = getAvatarState(user)
      return !state.error
    }

    const getAvatarSrc = (user) => {
      if (user.avatarUrl) {
        return user.avatarUrl
      }
      const state = getAvatarState(user)
      const ext = AVATAR_EXTENSIONS[state.extIndex] || 'webp'
      return `${avatarUrl.replace(/\/$/, '')}/avatar/${encodeURIComponent(user.username)}.${ext}`
    }

    const handleAvatarError = (user) => {
      const state = getAvatarState(user)
      if (user.avatarUrl) {
        // If the specific avatarUrl fails (e.g. server default.webp/default.svg not found),
        // clear it so we can fall back to local extensions or letter avatar
        user.avatarUrl = null
        state.extIndex = 0
      } else {
        state.extIndex++
      }
      if (state.extIndex >= AVATAR_EXTENSIONS.length) {
        state.error = true
      } else {
        avatarState[user.userId] = { ...state }
      }
    }

    return {
      closeModal,
      changePage,
      unfollow,
      viewProfile,
      getAvatarState,
      shouldShowAvatar,
      getAvatarSrc,
      handleAvatarError
    }
  }
}
</script>

<style scoped>
.fade-overlay-enter-active, .fade-overlay-leave-active { transition: opacity .25s ease; }
.fade-overlay-enter-from, .fade-overlay-leave-to { opacity: 0; }
.fade-overlay-enter-active .modal, .fade-overlay-leave-active .modal { transition: transform .24s ease, opacity .24s ease; }
.fade-overlay-enter-from .modal { transform: translateY(10px) scale(0.98); opacity: 0; }
.fade-overlay-leave-to .modal { transform: translateY(10px) scale(0.98); opacity: 0; }

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: var(--z-modal-elevated);
}

.follow-modal {
  max-width: 500px;
  width: 90%;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px) saturate(180%);
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: 20px;
  padding: 0;
  overflow: hidden;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.15);
  max-height: 80vh;
  display: flex;
  flex-direction: column;
}

.modal-header {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 25px;
  border-bottom: 1px solid rgba(0,0,0,0.05);
  background: white;
  z-index: 10;
}

.modal-header h3 {
  margin: 0;
  color: #333;
  font-size: 20px;
  font-weight: 600;
}

.close-btn {
  background: none;
  border: none;
  font-size: 24px;
  color: #999;
  cursor: pointer;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.close-btn:hover {
  background: rgba(0, 0, 0, 0.05);
  color: #333;
}

.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: #666;
}

.empty-icon {
  font-size: 48px;
  margin-bottom: 20px;
}

.follow-flex {
  padding: 20px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.follow-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: rgba(255, 255, 255, 0.6);
  border: 1px solid rgba(0,0,0,0.05);
  padding: 12px;
  border-radius: 12px;
  transition: all 0.3s ease;
}

.follow-item:hover {
  background: white;
  box-shadow: 0 4px 12px rgba(0,0,0,0.05);
  transform: translateY(-1px);
}

.avatar-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 15px;
}

.avatar-small-img {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid white;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.avatar-small {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 18px;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
}

.user-info {
  flex: 1;
  text-align: left;
}

.list-username {
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 4px 0;
  color: #333;
  cursor: pointer;
  transition: color 0.2s;
}

.list-username:hover {
  color: #667eea;
}

.status {
  font-size: 12px;
  color: #999;
}

.status.online {
  color: #4CAF50;
}

.actions {
  display: flex;
  gap: 8px;
  align-items: center;
}

.view-btn {
  background: #f0f2f5;
  color: #606266;
  border: none;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.view-btn:hover {
  background: #e4e7ed;
  color: #333;
}

.unfollow-btn {
  background: #fff;
  border: 1px solid #ff6b6b;
  color: #ff6b6b;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.unfollow-btn:hover {
  background: #fff5f5;
}

.pagination-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 15px;
  padding: 15px 20px;
  border-top: 1px solid rgba(0,0,0,0.05);
  background: rgba(250, 250, 250, 0.8);
}

.page-btn {
  padding: 6px 14px;
  border-radius: 16px;
  border: 1px solid #667eea;
  background: white;
  color: #667eea;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s;
}

.page-btn:hover:not(:disabled) {
  background: #667eea;
  color: white;
}

.page-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  border-color: #dcdfe6;
  color: #909399;
}

.page-info {
  font-size: 14px;
  color: #606266;
  font-weight: 500;
}

/* Dark Theme */
.dark-theme .follow-modal {
  background: rgba(45, 45, 45, 0.95);
  border-color: rgba(255, 255, 255, 0.1);
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.4);
}

.dark-theme .modal-header {
  background: rgba(45, 45, 45, 1);
  border-bottom-color: rgba(255, 255, 255, 0.05);
}

.dark-theme .modal-header h3 {
  color: #e0e0e0;
}

.dark-theme .close-btn:hover {
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
}

.dark-theme .empty-state p {
  color: #b0b0b0;
}

.dark-theme .follow-item {
  background: rgba(30, 30, 30, 0.6);
  border-color: rgba(255, 255, 255, 0.05);
}

.dark-theme .follow-item:hover {
  background: rgba(60, 60, 60, 0.8);
  box-shadow: 0 4px 12px rgba(0,0,0,0.2);
}

.dark-theme .list-username {
  color: #e0e0e0;
}

.dark-theme .list-username:hover {
  color: #b3c0ff;
}

.dark-theme .view-btn {
  background: rgba(255, 255, 255, 0.1);
  color: #e0e0e0;
}

.dark-theme .view-btn:hover {
  background: rgba(255, 255, 255, 0.2);
  color: #fff;
}

.dark-theme .unfollow-btn {
  background: transparent;
  border-color: #ff4d4f;
  color: #ff4d4f;
}

.dark-theme .unfollow-btn:hover {
  background: rgba(255, 77, 79, 0.1);
}

.dark-theme .pagination-wrapper {
  background: rgba(35, 35, 35, 0.8);
  border-top-color: rgba(255, 255, 255, 0.05);
}

.dark-theme .page-info {
  color: #e0e0e0;
}

.dark-theme .page-btn {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.2);
  color: #b3c0ff;
}

.dark-theme .page-btn:hover:not(:disabled) {
  background: #667eea;
  color: white;
  border-color: #667eea;
}
</style>
